import type { Todo } from '@/api'
import { buildTree, type TodoNode } from './tree'
import { dueTime } from './dates'
import { priorityRank } from './priority'
import { matchText } from './fuzzy'

export type StatusFilter = 'open' | 'all' | 'done'
export type GroupKey = 'none' | 'tag'
export type SortKey = 'manual' | 'due' | 'priority' | 'created' | 'title'

export interface ListViewOptions {
  status: StatusFilter
  search: string
  sort: SortKey
  tags?: readonly string[] // only the todos that carry all of these (casefolded), see `filterByTag`
}

// The tag name of a `/todos/:calendarId/tag/:tag` route param (an array if the route repeats the param).
export function tagFromParam(value: string | readonly string[] | undefined): string | null {
  const first = Array.isArray(value) ? value[0] : value
  const tag = (first ?? '').trim()
  return tag || null
}

export function isDone(todo: Todo): boolean {
  return todo.status === 'COMPLETED' || todo.status === 'CANCELLED'
}

// How well a todo matches the (lower case) search, the lower the better, null for no match. The summary and the tags
// match like the tag suggestions do (prefix, then anywhere, then the letters in order with gaps); the description,
// location and assignees only match as a substring, since the letters of a short search are found in almost any long
// text. At the same tier the summary comes before a tag, and a tag before the rest.
export function searchRank(todo: Todo, needle: string): number | null {
  if (!needle) return 0
  const ranks: number[] = []
  const summary = matchText(todo.summary, needle)
  if (summary) ranks.push(summary.tier * 3)
  for (const category of todo.categories) {
    const match = matchText(category, needle)
    if (match) ranks.push(match.tier * 3 + 1)
  }
  for (const text of [todo.description ?? '', todo.location ?? '', ...todo.assignees]) {
    if (text.toLocaleLowerCase().includes(needle)) ranks.push(1 * 3 + 2)
  }
  return ranks.length ? Math.min(...ranks) : null
}

function matchesStatus(todo: Todo, status: StatusFilter): boolean {
  if (status === 'all') return true
  return status === 'done' ? isDone(todo) : !isDone(todo)
}

// Keeps a node if it matches, or if any descendant does (so a matching subtask is still shown
// under its parent for context).
function filterNodes(nodes: TodoNode[], keep: (todo: Todo) => boolean): TodoNode[] {
  const result: TodoNode[] = []
  for (const node of nodes) {
    const children = filterNodes(node.children, keep)
    if (keep(node.todo) || children.length) result.push({ todo: node.todo, children })
  }
  return result
}

// Keeps a todo that carries `tag` with its whole subtree (its untagged subtasks belong to it), and a
// todo without the tag only as the parent of a descendant that has it (the other subtasks go).
function filterByTag(nodes: TodoNode[], tag: string): TodoNode[] {
  const needle = tag.toLocaleLowerCase()
  const result: TodoNode[] = []
  for (const node of nodes) {
    if (node.todo.categories.some((c) => c.toLocaleLowerCase() === needle)) {
      result.push(node)
      continue
    }
    const children = filterByTag(node.children, tag)
    if (children.length) result.push({ todo: node.todo, children })
  }
  return result
}

function nullsLast(a: number | null, b: number | null): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a - b
}

export function compareTodos(a: Todo, b: Todo, sort: SortKey): number {
  let result = 0
  switch (sort) {
    case 'manual':
      result = nullsLast(a.sort_order, b.sort_order)
      break
    case 'due':
      result = nullsLast(dueTime(a), dueTime(b))
      break
    case 'priority':
      result = priorityRank(a.priority) - priorityRank(b.priority)
      break
    case 'created':
      result = nullsLast(
        a.created ? Date.parse(a.created) : null,
        b.created ? Date.parse(b.created) : null,
      )
      break
    case 'title':
      result = a.summary.localeCompare(b.summary, undefined, { sensitivity: 'base', numeric: true })
      break
  }
  if (result !== 0) return result
  // Stable, predictable tie-breakers: flagged first, then open before done, then by title.
  if (a.flagged !== b.flagged) return a.flagged ? -1 : 1
  if (isDone(a) !== isDone(b)) return isDone(a) ? 1 : -1
  return a.summary.localeCompare(b.summary, undefined, { sensitivity: 'base', numeric: true })
}

function sortNodes(
  nodes: TodoNode[],
  sort: SortKey,
  rank: (node: TodoNode) => number = () => 0,
): TodoNode[] {
  return [...nodes]
    .sort((a, b) => rank(a) - rank(b) || compareTodos(a.todo, b.todo, sort))
    .map((n) => ({ todo: n.todo, children: sortNodes(n.children, sort, rank) }))
}

// The best rank of a node or any subtask of it, so a parent that is only there for a matching subtask sorts by it.
function nodeRank(node: TodoNode, needle: string): number {
  const own = searchRank(node.todo, needle) ?? Number.MAX_SAFE_INTEGER
  return Math.min(own, ...node.children.map((child) => nodeRank(child, needle)))
}

export function buildListView(todos: Todo[], options: ListViewOptions): TodoNode[] {
  const needle = options.search.trim().toLocaleLowerCase()
  const built = buildTree(todos)
  const tree = (options.tags ?? []).reduce(filterByTag, built)
  const filtered = filterNodes(
    tree,
    (t) => matchesStatus(t, options.status) && searchRank(t, needle) !== null,
  )
  return sortNodes(filtered, options.sort, needle ? (node) => nodeRank(node, needle) : undefined)
}

export interface NodeGroup {
  tag: string | null // null: no (usable) tag
  nodes: TodoNode[]
}

// Sections the top-level nodes by each of their tags in `categoryKeys` (casefolded names of the tags flagged
// "category"), so a todo can be in several sections; one without such a tag goes by its first tag that is not
// in `ignoreKeys` (the tags flagged "ignore for grouping"). A subtask stays under its parent. Sections are
// alphabetical, the untagged one is last; inside a section the given order is kept.
export function groupNodes(
  nodes: TodoNode[],
  ignoreKeys: ReadonlySet<string>,
  categoryKeys: ReadonlySet<string> = new Set(),
): NodeGroup[] {
  const byKey = new Map<string, NodeGroup>()
  const untagged: NodeGroup = { tag: null, nodes: [] }
  for (const node of nodes) {
    const counting = node.todo.categories.filter((c) => !ignoreKeys.has(c.toLocaleLowerCase()))
    const categories = counting.filter((c) => categoryKeys.has(c.toLocaleLowerCase()))
    const chosen = categories.length ? categories : counting.slice(0, 1)
    if (!chosen.length) {
      untagged.nodes.push(node)
      continue
    }
    const placed = new Set<string>()
    for (const tag of chosen) {
      const key = tag.toLocaleLowerCase()
      if (placed.has(key)) continue
      placed.add(key)
      const group = byKey.get(key) ?? { tag, nodes: [] }
      group.nodes.push(node)
      byKey.set(key, group)
    }
  }
  const tagged = [...byKey.values()].sort((a, b) =>
    a.tag!.localeCompare(b.tag!, undefined, { sensitivity: 'base' }),
  )
  return untagged.nodes.length ? [...tagged, untagged] : tagged
}

// New manual order after dragging `movedUid` to `newIndex` among `siblings` (already in their
// current display order). Returns only the todos whose sort_order actually has to change.
// Like Apple Reminders only the moved todo gets a new number: one below the first, one above the
// last, or the (rounded down) middle between its neighbours. Without such a number to take (a
// neighbour has none, or there is no whole number between them) all siblings are renumbered.
export function reorderSiblings(
  siblings: Todo[],
  movedUid: string,
  newIndex: number,
): { uid: string; sort_order: number }[] {
  const ordered = siblings.filter((t) => t.uid !== movedUid)
  const moved = siblings.find((t) => t.uid === movedUid)
  if (!moved) return []
  const index = Math.max(0, Math.min(newIndex, ordered.length))
  ordered.splice(index, 0, moved)
  const before = index > 0 ? ordered[index - 1].sort_order : undefined
  const after = index < ordered.length - 1 ? ordered[index + 1].sort_order : undefined
  if (before !== null && after !== null) {
    if (
      moved.sort_order !== null &&
      (before === undefined || moved.sort_order > before) &&
      (after === undefined || moved.sort_order < after)
    ) {
      return []
    }
    if (before === undefined && after === undefined) return []
    if (before === undefined) return [{ uid: movedUid, sort_order: after! - 1 }]
    if (after === undefined) return [{ uid: movedUid, sort_order: before + 1 }]
    if (after - before >= 2) return [{ uid: movedUid, sort_order: Math.floor((before + after) / 2) }]
  }
  const changes: { uid: string; sort_order: number }[] = []
  ordered.forEach((todo, position) => {
    const sortOrder = (position + 1) * 10
    if (todo.sort_order !== sortOrder) changes.push({ uid: todo.uid, sort_order: sortOrder })
  })
  return changes
}
